import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { teamCopy } from "../../lib/teamCopy";
import { BottomSheet, ChalkActionButton } from "./primitives";

/**
 * Add-player sheet — branches on team_type:
 *   - Youth: collect guardian first/last/email (parent gets invited).
 *   - Adult: collect player email (the player invites themselves).
 *
 * Backend contract: POST /roster/{teamId}/athletes with either
 * `{guardian_*}` (youth) or `{player_email}` (adult). Server figures out the
 * right invite kind from the team's stored team_type, so we don't need to
 * send it explicitly.
 */
export default function AddAthleteSheet({ isOpen, onDismiss, team, teamId, onCreated }) {
  const copy = teamCopy(team);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [jersey, setJersey] = useState("");
  const [position, setPosition] = useState("");
  const [inviteeFirst, setInviteeFirst] = useState("");
  const [inviteeLast, setInviteeLast] = useState("");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const actualTeamId = teamId || (team && team.id);

  async function submit() {
    if (!actualTeamId) {
      toast.error("Create a team first.");
      return;
    }
    if (!first.trim() || !last.trim()) {
      toast.error("Both first and last name are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: first.trim(),
        last_name: last.trim(),
        jersey: jersey.trim() || null,
        position: position.trim() || null,
      };
      if (copy.isAdult) {
        payload.player_email = inviteeEmail.trim() || null;
      } else {
        payload.guardian_first_name = inviteeFirst.trim() || null;
        payload.guardian_last_name = inviteeLast.trim() || null;
        payload.guardian_email = inviteeEmail.trim() || null;
      }
      const data = await api.addAthlete(actualTeamId, payload);
      const invited = Boolean(data.guardian_invite_token);
      const subject = copy.isAdult ? "player" : "guardian";
      toast.success(invited
        ? `${first.trim()} added — invite sent to the ${subject}.`
        : `${first.trim()} added to roster.`);
      onCreated && onCreated(data.athlete);
      setFirst(""); setLast(""); setJersey(""); setPosition("");
      setInviteeFirst(""); setInviteeLast(""); setInviteeEmail("");
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't add player.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Add a player" onDismiss={onDismiss}>
      <div data-testid="add-athlete-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="First name" value={first} onChange={setFirst} placeholder="Mia" testId="athlete-first" autoFocus />
          <Field label="Last name" value={last} onChange={setLast} placeholder="Lopez" testId="athlete-last" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
          <Field label="Jersey #" value={jersey} onChange={setJersey} placeholder="7" testId="athlete-jersey" />
          <Field label="Position" value={position} onChange={setPosition} placeholder="Shortstop" testId="athlete-position" />
        </div>

        {/* Invite block — copy adapts to team_type. Youth collects guardian
            name + email; adult collects only the player's email since the
            player IS the athlete. */}
        <div style={{
          background: "rgba(46,125,82,0.05)",
          border: "0.5px solid rgba(46,125,82,0.18)",
          borderRadius: 10, padding: "12px 12px 14px",
        }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em", color: "var(--green-mid)", fontWeight: 700, marginBottom: 8 }}>
            Invite the {copy.inviteeSingularLower}{" "}
            <span style={{ opacity: 0.6, fontWeight: 500 }}>{copy.isAdult ? "" : "(optional)"}</span>
          </div>
          {!copy.isAdult && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Field
                  label={copy.inviteeFirstNameLabel}
                  value={inviteeFirst} onChange={setInviteeFirst}
                  placeholder="Sarah" testId="athlete-guardian-first"
                />
                <Field
                  label={copy.inviteeLastNameLabel}
                  value={inviteeLast} onChange={setInviteeLast}
                  placeholder="Lopez" testId="athlete-guardian-last"
                />
              </div>
              <div style={{ height: 8 }} />
            </>
          )}
          <Field
            label={copy.inviteeEmailLabel}
            value={inviteeEmail} onChange={setInviteeEmail}
            placeholder={copy.isAdult ? "jordan@email.com" : "sarah@email.com"}
            testId={copy.isAdult ? "athlete-player-email" : "athlete-guardian-email"}
            type="email"
          />
          <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 8, lineHeight: 1.4 }}>
            {copy.inviteSentCopy}
          </div>
        </div>

        <ChalkActionButton
          testId="athlete-submit"
          variant="primary"
          label={saving
            ? "Adding…"
            : (inviteeEmail.trim() ? "Add player & send invite" : "Add to roster")}
          icon={<Plus size={16} aria-hidden="true" />}
          loading={saving}
          onClick={submit}
        />
      </div>
    </BottomSheet>
  );
}

function Field({ label, value, onChange, placeholder, testId, autoFocus, type = "text" }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>
        {label}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        autoComplete={type === "email" ? "email" : "off"}
        style={{
          width: "100%", border: "0.5px solid var(--chalk-border)",
          borderRadius: 10, padding: "11px 12px", fontSize: 16,
          fontFamily: "inherit", outline: "none",
          background: "#FBF8F2", color: "var(--chalk-board)",
        }}
      />
    </label>
  );
}
